/**
 * Zentrale Download-Utility für Desktop und Mobile Browser.
 * Löst das Problem, dass Downloads auf Desktop-Browsern nicht funktionieren.
 * 
 * KRITISCHE REGELN:
 * 1. downloadBlob muss synchron im User-Gesture-Kontext aufgerufen werden
 * 2. Kein setTimeout VOR dem a.click()
 * 3. Blob-URL muss NACH dem Click-Event aufgeräumt werden
 */

/**
 * Triggert einen echten Download aus einem Blob.
 * Funktioniert auf Desktop (Chrome/Edge/Firefox) und Mobile (iOS/Android).
 * 
 * @param {Blob} blob - Der herunterzuladende Blob
 * @param {string} filename - Dateiname für den Download
 */
export const downloadBlob = (blob, filename) => {
  // Erstelle Object-URL
  const url = URL.createObjectURL(blob);
  
  // Erstelle temporäres <a>-Element
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  // Füge zum DOM hinzu (wichtig für Firefox)
  document.body.appendChild(link);
  
  // Triggere den Download SYNCHRON (User Gesture!)
  link.click();
  
  // Entferne Link sofort (Download ist bereits gestartet)
  document.body.removeChild(link);
  
  // Räume Blob-URL NACH dem Download auf
  // Verzögerung nötig, damit der Browser Zeit hat, den Download zu starten
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
};

/**
 * Lädt eine CSV-Datei vom Backend herunter.
 * Verwendet die übergebene axios-Instanz (mit Auth-Interceptor).
 * 
 * WICHTIG: Diese Funktion muss direkt im onClick-Handler mit await aufgerufen werden!
 * 
 * @param {object} axiosInstance - Die konfigurierte axios-Instanz aus der App
 * @param {string} url - Die vollständige URL zum CSV-Endpoint
 * @param {string} filename - Dateiname für den Download
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const downloadCsv = async (axiosInstance, url, filename) => {
  try {
    const response = await axiosInstance.get(url, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv, application/octet-stream'
      }
    });
    
    // Prüfe Content-Type: Wenn JSON, dann ist es ein Fehler
    const contentType = response.headers['content-type'] || '';
    
    if (contentType.includes('application/json')) {
      // Backend hat JSON statt CSV geliefert (Fehler)
      const text = await response.data.text();
      try {
        const errorData = JSON.parse(text);
        return { 
          success: false, 
          error: errorData.detail || 'Server hat einen Fehler zurückgegeben'
        };
      } catch {
        return { success: false, error: 'Unerwartete Server-Antwort' };
      }
    }
    
    // Versuche Filename aus Content-Disposition zu extrahieren
    let finalFilename = filename;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        finalFilename = match[1].replace(/['"]/g, '');
      }
    }
    
    // Erstelle Blob mit korrektem MIME-Type
    const blob = new Blob([response.data], { 
      type: 'text/csv;charset=utf-8;' 
    });
    
    // Triggere Download SYNCHRON
    downloadBlob(blob, finalFilename);
    
    return { success: true };
    
  } catch (error) {
    console.error('[downloadCsv] Fehler:', error);
    
    // Extrahiere Fehlermeldung
    if (error.response) {
      if (error.response.status === 401) {
        return { success: false, error: 'Nicht authentifiziert' };
      }
      if (error.response.status === 403) {
        return { success: false, error: 'Keine Berechtigung für CSV Export' };
      }
      // Versuche Detail aus Response zu lesen
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const data = JSON.parse(text);
          return { success: false, error: data.detail || 'Server-Fehler' };
        } catch {
          // Ignorieren
        }
      }
    }
    
    return { 
      success: false, 
      error: error.message || 'Fehler beim Herunterladen'
    };
  }
};

/**
 * Generiert CSV-Inhalt aus Daten und triggert Download.
 * Für client-seitig generierte CSVs.
 * 
 * @param {Array} data - Array von Objekten
 * @param {Array<string>} headers - Header-Namen für CSV
 * @param {Array<string>} keys - Objekt-Keys (entsprechend headers)
 * @param {string} filename - Dateiname
 * @returns {{success: boolean, error?: string}}
 */
export const generateAndDownloadCsv = (data, headers, keys, filename) => {
  try {
    if (!data || data.length === 0) {
      return { success: false, error: 'Keine Daten zum Exportieren' };
    }
    
    // BOM für Excel UTF-8 Erkennung
    const BOM = '\uFEFF';
    
    // Header-Zeile
    const headerLine = headers.join(';');
    
    // Daten-Zeilen
    const lines = data.map(item => {
      return keys.map(key => {
        let value = item[key];
        
        // Verschachtelte Objekte (z.B. "location.name")
        if (key.includes('.')) {
          const parts = key.split('.');
          value = parts.reduce((obj, part) => obj?.[part], item);
        }
        
        // Werte formatieren
        if (value === null || value === undefined) {
          return '';
        }
        if (typeof value === 'number') {
          return value.toFixed(2).replace('.', ','); // Deutsches Format
        }
        
        // Strings mit Sonderzeichen escapen
        const strValue = String(value);
        if (strValue.includes(';') || strValue.includes('"') || strValue.includes('\n')) {
          return `"${strValue.replace(/"/g, '""')}"`;
        }
        return strValue;
      }).join(';');
    });
    
    const csvContent = BOM + headerLine + '\n' + lines.join('\n');
    
    // Blob erstellen und Download triggern
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, filename);
    
    return { success: true };
    
  } catch (error) {
    console.error('[generateAndDownloadCsv] Fehler:', error);
    return { success: false, error: error.message || 'Fehler beim Generieren' };
  }
};

export default { downloadBlob, downloadCsv, generateAndDownloadCsv };
